import React, { useState } from "react";
import { API_BASE } from "../api/config";
import DateiBrowser from "../components/ki/DateiBrowser";
import FehlerMarkierer from "../components/ki/FehlerMarkierer";
import ChatFenster from "../components/ki/ChatFenster";
import "./KiLernen.css";

const API = `${API_BASE}/ki`;

// Drei Schritte: Datei → Fehler markieren → KI-Dialog
const SCHRITTE = ["datei", "fehler", "lernen"];

export default function KiLernen() {
  const [schritt, setSchritt] = useState("datei");
  const [gewaehlteDate, setGewaehlteDate] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [starteNachricht, setStarteNachricht] = useState(null);
  const [starten, setStarten] = useState(false);
  const [fehler, setFehler] = useState(null);
  const [gelernteRegel, setGelernteRegel] = useState(null);
  const [sessionAbgeschlossen, setSessionAbgeschlossen] = useState(false);

  // Schritt 1 → 2: Datei ausgewählt
  const onDateiGewaehlt = (datei) => {
    setGewaehlteDate(datei);
    setSchritt("fehler");
    setFehler(null);
  };

  // Schritt 2 → 3: Fehler bestätigt → Lern-Session starten
  const onFehlerBestaetigt = async (fehlerListe) => {
    if (!gewaehlteDate) return;
    setStarten(true);
    setFehler(null);
    try {
      const res = await fetch(`${API}/lern/session/start`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          dropbox_path: gewaehlteDate.path,
          wrong_fields: fehlerListe,
          current_result: gewaehlteDate.current_result || null,
          pdf_text: gewaehlteDate.text_preview || null,
        }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setSessionId(data.session_id);
      setStarteNachricht(data.message);
      setSchritt("lernen");
    } catch (e) {
      setFehler("Session konnte nicht gestartet werden: " + e.message);
    }
    setStarten(false);
  };

  // Schritt zurück
  const zurueck = () => {
    if (schritt === "fehler") { setSchritt("datei"); setGewaehlteDate(null); }
    if (schritt === "lernen") { setSchritt("fehler"); setSessionId(null); }
  };

  // Neue Lernrunde
  const neuStart = () => {
    setSchritt("datei");
    setGewaehlteDate(null);
    setSessionId(null);
    setStarteNachricht(null);
    setGelernteRegel(null);
    setSessionAbgeschlossen(false);
    setFehler(null);
  };

  return (
    <div className="ki-lernen-seite">
      {/* Header */}
      <div className="ki-lernen-header">
        <h1>🤖 KI Lernsektion</h1>
        <p className="ki-untertitel">
          Markiere Fehler in falsch umbenannten Dateien — die KI analysiert selbst
          warum sie falsch lag und lernt für die Zukunft.
        </p>

        {/* Fortschrittsanzeige */}
        <div className="fortschritt-leiste">
          {[
            { key: "datei",  label: "1. Datei wählen", icon: "📄" },
            { key: "fehler", label: "2. Fehler markieren", icon: "✗" },
            { key: "lernen", label: "3. KI lernt", icon: "🤖" },
          ].map((s) => (
            <div
              key={s.key}
              className={`fortschritt-schritt ${
                schritt === s.key ? "aktiv" :
                SCHRITTE.indexOf(schritt) > SCHRITTE.indexOf(s.key) ? "fertig" : ""
              }`}
            >
              <span className="schritt-icon">{s.icon}</span>
              <span className="schritt-label">{s.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Haupt-Layout */}
      <div className="ki-lernen-inhalt">

        {/* ── Schritt 1: Datei wählen ── */}
        {schritt === "datei" && (
          <div className="schritt-bereich">
            <h2>Datei aus Dropbox wählen</h2>
            <p className="schritt-beschreibung">
              Wähle eine falsch umbenannte PDF-Datei aus den NOK- oder Fehler-Ordnern.
            </p>
            <DateiBrowser onFileSelect={onDateiGewaehlt} />
          </div>
        )}

        {/* ── Schritt 2: Fehler markieren ── */}
        {schritt === "fehler" && gewaehlteDate && (
          <div className="schritt-bereich">
            <div className="schritt-nav">
              <button className="zurueck-btn" onClick={zurueck}>← Zurück</button>
            </div>
            <h2>Fehler markieren</h2>
            <div className="datei-info-box">
              <span className="datei-info-icon">📄</span>
              <div>
                <div className="datei-info-name">{gewaehlteDate.name || gewaehlteDate.filename}</div>
                {gewaehlteDate.text_preview && (
                  <details className="text-vorschau">
                    <summary>PDF-Text Vorschau</summary>
                    <pre>{gewaehlteDate.text_preview.slice(0, 800)}</pre>
                  </details>
                )}
              </div>
            </div>

            {fehler && <div className="fehler-meldung">⚠️ {fehler}</div>}

            <FehlerMarkierer
              dateiname={gewaehlteDate.name || gewaehlteDate.filename}
              onFehlerBestaetigt={onFehlerBestaetigt}
            />

            {starten && (
              <div className="laden-overlay">
                <span>⏳ KI wird gestartet...</span>
              </div>
            )}
          </div>
        )}

        {/* ── Schritt 3: KI-Dialog ── */}
        {schritt === "lernen" && sessionId && (
          <div className="schritt-bereich lern-dialog">
            <div className="schritt-nav">
              <button className="zurueck-btn" onClick={zurueck}>← Zurück</button>
              {sessionAbgeschlossen && (
                <button className="neu-btn" onClick={neuStart}>🔄 Neue Datei</button>
              )}
            </div>

            <div className="lern-layout">
              {/* Linke Seite: Datei-Info */}
              <div className="lern-info">
                <h3>📄 {gewaehlteDate?.name || gewaehlteDate?.filename}</h3>
                {gewaehlteDate?.text_preview && (
                  <details className="text-vorschau kompakt" open>
                    <summary>PDF-Text</summary>
                    <pre>{gewaehlteDate.text_preview.slice(0, 1000)}</pre>
                  </details>
                )}
                {gelernteRegel && (
                  <div className="gelernte-regel">
                    <h4>📝 Gelernte Regel:</h4>
                    <p>{gelernteRegel}</p>
                  </div>
                )}
              </div>

              {/* Rechte Seite: Chat */}
              <div className="lern-chat">
                <ChatFenster
                  key={sessionId}
                  sessionId={sessionId}
                  mode="lern"
                  initialMessage={starteNachricht}
                  onDone={() => setSessionAbgeschlossen(true)}
                  onLearnedRule={setGelernteRegel}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
